<template>
  <APageHead
    :title="$t('label.142')"
    :sub="$t('txt.26')"
    icon="account_balance_wallet"
  >
    <template #actions>
      <ABtn kind="ghost" icon="refresh" @click="getSummary">
        {{ $t('label.79') }}
      </ABtn>
    </template>
  </APageHead>

  <div class="ss-page">
    <!-- 정산 요약 -->
    <div class="ss-stats">
      <AStat
        :label="$t('label.143')"
        :value="formatCurrency(summary.totalRevenue)"
        icon="savings"
      />
      <!-- 이번 달에 '지급 완료'된 금액 — 정산_프론트엔드개발_가이드.md §12.1 -->
      <AStat
        :label="$t('label.144')"
        :value="formatCurrency(summary.monthlyRevenue)"
        icon="payments"
        :hint="$t('txt.28')"
      />
      <AStat
        :label="$t('label.145')"
        :value="formatCurrency(summary.pendingSettlement)"
        icon="pending"
        :accent="Number(summary.pendingSettlement) > 0"
      />
    </div>

    <!-- 최근 정산 내역 -->
    <ACard :title="$t('label.146')" :padding="0">
      <ATable
        :columns="settlementCols"
        :rows="recentSettlements"
        row-key="idx"
        :empty-text="$t('txt.27')"
      >
        <template #cell:period="{ row }">
          <span>{{ row.settlementYear }}년 {{ row.settlementMonth }}월</span>
        </template>

        <template #cell:textbook="{ row }">
          <span class="ss-textbook">{{ row.textbookTitle || '-' }}</span>
        </template>

        <template #cell:amount="{ row }">
          <span class="ss-amount">
            {{ formatCurrency(row.royaltyAmount, row.royaltyCurrency) }}
          </span>
        </template>

        <template #cell:status="{ row }">
          <APill :kind="settlementStatusPillKind(row.status)">
            {{ getSettlementStatusLabel(row.status) }}
          </APill>
        </template>

        <template #cell:paidAt="{ row }">
          <span class="ss-date">{{ formatDateTime(row.paidAt) }}</span>
        </template>
      </ATable>
    </ACard>

    <!-- 계좌 정보 -->
    <ACard :title="$t('label.147')" :padding="20">
      <div class="ss-form-grid">
        <div class="p-field">
          <label class="p-field__label" for="ss-bank-name">
            <i class="material-icons-round p-field__label-icon">account_balance</i>
            {{ $t('label.148') }} <span class="p-field__req">*</span>
          </label>
          <select
            id="ss-bank-name"
            class="p-input"
            v-model="bankInfo.bankName"
            :class="{ 'is-error': errors.bankName }"
          >
            <option value="">{{ $t('label.148') }}</option>
            <option v-for="bank in bankOptions" :key="bank" :value="bank">{{ bank }}</option>
          </select>
          <div v-if="errors.bankName" class="p-field__error">{{ errors.bankName }}</div>
        </div>

        <div class="p-field">
          <label class="p-field__label" for="ss-account-number">
            <i class="material-icons-round p-field__label-icon">tag</i>
            {{ $t('label.149') }} <span class="p-field__req">*</span>
          </label>
          <input
            id="ss-account-number"
            type="text"
            class="p-input"
            :class="{ 'is-error': errors.accountNumber }"
            v-model.trim="bankInfo.accountNumber"
            placeholder="123-456-789012"
          />
          <div v-if="errors.accountNumber" class="p-field__error">{{ errors.accountNumber }}</div>
        </div>

        <div class="p-field">
          <label class="p-field__label" for="ss-account-holder">
            <i class="material-icons-round p-field__label-icon">person</i>
            {{ $t('label.150') }} <span class="p-field__req">*</span>
          </label>
          <input
            id="ss-account-holder"
            type="text"
            class="p-input"
            :class="{ 'is-error': errors.accountHolder }"
            v-model.trim="bankInfo.accountHolder"
            maxlength="50"
          />
          <div v-if="errors.accountHolder" class="p-field__error">{{ errors.accountHolder }}</div>
        </div>

        <div class="p-field">
          <label class="p-field__label" for="ss-currency">
            <i class="material-icons-round p-field__label-icon">currency_exchange</i>
            {{ $t('label.151') }}
          </label>
          <select id="ss-currency" class="p-input" v-model="bankInfo.preferredCurrency">
            <option v-for="cur in currencyOptions" :key="cur" :value="cur">{{ cur }}</option>
          </select>
        </div>
      </div>

      <template #foot>
        <ABtn kind="primary" icon="save" :loading="saving" @click="saveBankInfo">
          {{ $t('label.152') }}
        </ABtn>
      </template>
    </ACard>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: 'SettlementSummary',
  components: { APageHead, ACard, AStat, ATable, APill, ABtn },

  data() {
    return {
      saving: false,
      summary: {
        editorName: '',
        totalRevenue: 0,
        monthlyRevenue: 0,
        pendingSettlement: 0,
        currency: 'KRW',
      },
      recentSettlements: [],
      bankInfo: {
        bankName: '',
        accountNumber: '',
        accountHolder: '',
        preferredCurrency: 'KRW',
      },
      errors: {
        bankName: '',
        accountNumber: '',
        accountHolder: '',
      },
      // 정산_에디터_API.md - 지원 은행 21개
      bankOptions: [
        'KB국민은행', '신한은행', '우리은행', '하나은행', 'SC제일은행',
        '한국씨티은행', '케이뱅크', '카카오뱅크', '토스뱅크', 'NH농협은행',
        '수협은행', '대구은행', '부산은행', '광주은행', '전북은행',
        '경남은행', '제주은행', 'IBK기업은행', 'KDB산업은행', '새마을금고',
        '우체국',
      ],
      currencyOptions: ['KRW', 'USD', 'JPY', 'CNY', 'EUR'],
    };
  },

  computed: {
    // $t 를 computed 로 두어야 언어 변경 시 헤더가 함께 갱신된다
    settlementCols() {
      return [
        { key: 'period',   label: this.$t('label.94'),  align: 'left',   width: 130 },
        { key: 'textbook', label: this.$t('label.153'), align: 'left' },
        { key: 'amount',   label: this.$t('label.132'), align: 'right',  width: 160 },
        { key: 'status',   label: this.$t('label.51'),  align: 'center', width: 120 },
        { key: 'paidAt',   label: this.$t('label.154'), align: 'left',   width: 170 },
      ];
    },
    // 소유자 검증이 실제로 동작하므로 항상 로그인한 본인의 editor_idx 를 사용한다
    // (정산_프론트엔드개발_가이드.md §12.2 - 타인 idx 를 넘기면 403)
    editorIdx() {
      return this.$store.state.auth?.user_info?.idx || null;
    },
  },

  mounted() {
    this.getSummary();
  },

  methods: {
    async getSummary() {
      if (!this.editorIdx) {
        this.$notifyVue({
          type: 'danger',
          title: this.$t('alert.04'),
          text: this.$t('txt.01'),
        });
        return;
      }

      try {
        const res = await this.$axios({
          method: 'get',
          url: `/settlement/editor/${this.editorIdx}/summary`,
          loadingBar: true,
        });

        const body = res.data || {};
        if (res.status === 200 && body.success !== false) {
          const data = body.data || body;
          this.summary = {
            editorName: data.editorName || '',
            totalRevenue: data.totalRevenue || 0,
            monthlyRevenue: data.monthlyRevenue || 0,
            pendingSettlement: data.pendingSettlement || 0,
            currency: data.currency || 'KRW',
          };
          this.recentSettlements = data.recentSettlements || [];
          if (data.bankInfo) {
            const currency = data.bankInfo.preferredCurrency || 'KRW';
            // 목록에 없는 통화를 쓰는 에디터도 현재 값이 select 에 보이도록 추가
            if (!this.currencyOptions.includes(currency)) {
              this.currencyOptions = [...this.currencyOptions, currency];
            }
            this.bankInfo = {
              bankName: data.bankInfo.bankName || '',
              accountNumber: data.bankInfo.accountNumber || '',
              accountHolder: data.bankInfo.accountHolder || '',
              preferredCurrency: currency,
            };
          }
        } else {
          // HTTP 200 + success:false 로 내려오는 경우도 조용히 넘기지 않는다
          this.$notifyVue({
            type: 'danger',
            title: this.$t('alert.04'),
            text: body.error?.message || body.message || this.$t('alert.50'),
          });
        }
      } catch (error) {
        console.error('정산 요약 조회 실패:', error);
        this.$notifyVue({
          type: 'danger',
          title: this.$t('alert.04'),
          text: this.errorMessage(error),
        });
      }
    },

    validate() {
      this.errors = { bankName: '', accountNumber: '', accountHolder: '' };

      if (!this.bankInfo.bankName) {
        this.errors.bankName = this.$t('alert.71', { obj: this.$t('label.148') });
      }
      if (!this.bankInfo.accountNumber) {
        this.errors.accountNumber = this.$t('alert.71', { obj: this.$t('label.149') });
      } else if (!/^[0-9-]+$/.test(this.bankInfo.accountNumber)) {
        this.errors.accountNumber = this.$t('txt.29');
      }
      if (!this.bankInfo.accountHolder) {
        this.errors.accountHolder = this.$t('alert.71', { obj: this.$t('label.150') });
      }

      return !this.errors.bankName && !this.errors.accountNumber && !this.errors.accountHolder;
    },

    async saveBankInfo() {
      if (!this.editorIdx) {
        this.$notifyVue({
          type: 'danger',
          title: this.$t('label.152'),
          text: this.$t('txt.01'),
        });
        return;
      }
      if (!this.validate()) return;

      this.saving = true;
      try {
        const res = await this.$axios({
          method: 'put',
          url: `/settlement/editor/${this.editorIdx}/bank-info`,
          data: {
            bankName: this.bankInfo.bankName,
            accountNumber: this.bankInfo.accountNumber,
            accountHolder: this.bankInfo.accountHolder,
            preferredCurrency: this.bankInfo.preferredCurrency || 'KRW',
          },
          loadingBar: true,
        });

        const body = res.data || {};
        if (res.status === 200 && body.success !== false) {
          this.$notifyVue({
            type: 'success',
            title: this.$t('label.152'),
            text: body.message || this.$t('alert.19'),
          });
          // 선호 화폐를 바꾸면 요약 금액의 표기 화폐도 달라지므로 재조회
          await this.getSummary();
        } else {
          this.$notifyVue({
            type: 'danger',
            title: this.$t('label.152'),
            text: body.error?.message || body.message || this.$t('alert.50'),
          });
        }
      } catch (error) {
        console.error('계좌 정보 저장 실패:', error);
        // 은행명/계좌번호 검증(400)·소유자 검증(403) 메시지는 그대로 노출한다 (가이드 §7, §12.2)
        this.$notifyVue({
          type: 'danger',
          title: this.$t('label.152'),
          text: this.errorMessage(error),
        });
      } finally {
        this.saving = false;
      }
    },

    errorMessage(error) {
      const data = error?.response?.data;
      // 백엔드 규약: FastAPI 는 detail, 정산 API 는 error.message 를 내려준다
      return data?.detail || data?.error?.message || data?.message || this.$t('alert.50');
    },

    formatCurrency(amount, currencyCode) {
      const value = Number(amount) || 0;
      const code = currencyCode || this.summary.currency || 'KRW';
      try {
        return new Intl.NumberFormat('ko-KR', {
          style: 'currency',
          currency: code,
          minimumFractionDigits: code === 'KRW' ? 0 : 2,
        }).format(value);
      } catch (error) {
        return `${value.toLocaleString('ko-KR')} ${code}`;
      }
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    },

    settlementStatusPillKind(status) {
      const map = { calculated: 'info', invoiced: 'warn', paid: 'ok' };
      return map[status] || 'default';
    },

    getSettlementStatusLabel(status) {
      const labels = {
        calculated: this.$t('label.136'),
        invoiced: this.$t('label.137'),
        paid: this.$t('label.82'),
      };
      return labels[status] || status;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/pineapple';

.ss-page {
  padding: 20px 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: $p-font-body;
}

.ss-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.ss-form-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0 16px;
}

.ss-textbook {
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
}

.ss-amount {
  font-family: $p-font-mono;
  font-size: 13px;
  font-weight: 800;
  color: $p-ok;
}

.ss-date {
  font-family: $p-font-mono;
  font-size: 12px;
  color: $p-ink-mid;
}

@media (max-width: 1100px) {
  .ss-stats     { grid-template-columns: 1fr; }
  .ss-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 700px) {
  .ss-form-grid { grid-template-columns: 1fr; }
}
</style>
